import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Icon } from './Icon';

/**
 * حاجز أخطاء حول محتوى الصفحة.
 *
 * خطأٌ غير ملتقَط أثناء التصيير يجعل React يُسقط الشجرة كلها، فتصير الشاشة
 * بيضاء بلا شريط ولا قائمة ولا رسالة — ولا يعرف من يراها إن كان النظام
 * تعطّل أم خرجت جلسته. وقع هذا فعلًا: مكوّنٌ نادى `flatMap` على كائن أعاده
 * الخادم، فأغلقت شاشة إدارة العملاء نفسها.
 *
 * والحاجز حول `Outlet` لا حول التطبيق كله عمدًا: الشريط والقائمة الجانبية
 * يبقيان، فيستطيع من وقع في الخطأ أن ينتقل إلى شاشة أخرى بدل أن يعيد تحميل
 * الصفحة أو يظن أن النظام سقط.
 *
 * ولا يُغني عن معالجة الأخطاء في مواضعها: هو شبكة أمان لما لم يُتوقَّع، لا
 * بديل عن `ErrorState` لأخطاء الطلبات المعروفة.
 *
 * (حاجز الأخطاء لا يكون إلا صنفًا: `componentDidCatch` و`getDerivedStateFromError`
 * لا مقابل لهما في الدوال حتى اليوم.)
 */

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<
  { children: ReactNode; /** يُعيد ضبط الحاجز عند تغيّره — المسار عادةً. */ resetKey?: string },
  State
> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /*
     * السجل في وحدة التحكّم فقط.
     *
     * لا خدمة تجميع أخطاء في المشروع، وإرسالها إلى الخادم يحتاج مسارًا
     * ونطاق مستأجر وقرارًا فيما يُحفظ — وهو أوسع من هذا الحاجز.
     */
    console.error('خطأ غير ملتقَط في الشاشة:', error, info.componentStack);
  }

  override componentDidUpdate(prev: { resetKey?: string }): void {
    /* الانتقال إلى شاشة أخرى يمحو الخطأ: لا معنى لبقائه بعد مغادرة موضعه. */
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="page">
        <div className="alert alert-danger">
          <Icon name="alert" size={18} />
          <div>
            <strong>تعذّر عرض هذه الشاشة.</strong>
            <div className="tiny mt-2">
              وقع خطأ لم يتوقّعه النظام، فأُوقف عرض هذه الشاشة وحدها وبقي ما
              عداها يعمل. جرّب شاشة أخرى من القائمة، أو أعد تحميل الصفحة.
            </div>
          </div>
        </div>

        {/*
          نصّ الخطأ معروض لا مخفيّ.
          من يبلّغ عن عطل يحتاج ما ينسخه، ورسالةٌ عامة بلا تفصيل تجعل كل
          بلاغ يبدأ من الصفر.
        */}
        <details className="card">
          <summary className="card-body" style={{ cursor: 'pointer' }}>
            تفاصيل تقنية — انسخها عند الإبلاغ
          </summary>
          <div className="card-body">
            <pre className="tiny" style={{ whiteSpace: 'pre-wrap', direction: 'ltr' }}>
              {error.message}
            </pre>
          </div>
        </details>

        <button className="btn btn-primary mt-4" onClick={() => window.location.reload()}>
          <Icon name="refresh" size={15} />
          إعادة تحميل
        </button>
      </div>
    );
  }
}
